/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled, { keyframes } from 'styled-components';
import { Ring } from '@uiball/loaders';
import { getStoreInfoById, getMyStore, getMyProduct, createProduct, getCategory, addAuction, cloneProduct, importProduct, fetchMyStoreLot } from '../../../../utils/services/user.service';
import Select from 'react-select';
import StoreDetailNewView from './StoreDetailNew';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #0B1122;
  color: white;
`;

const HeroSection = styled.div`
  position: relative;
  height: 260px;
  overflow: hidden;
  background: linear-gradient(135deg, #ff6b35 0%, #d2691e 100%);
`;

const HeroImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.3;
`;

const HeroPattern = styled.div`
  position: absolute;
  inset: 0;
  background-image: 
    radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.1) 2px, transparent 2px),
    radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.1) 2px, transparent 2px),
    radial-gradient(circle at 40% 60%, rgba(255, 255, 255, 0.1) 2px, transparent 2px),
    radial-gradient(circle at 60% 20%, rgba(255, 255, 255, 0.1) 2px, transparent 2px);
  background-size: 50px 50px, 60px 60px, 40px 40px, 55px 55px;
  background-position: 0 0, 25px 25px, 50px 50px, 75px 75px;
  opacity: 0.5;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 10;
  padding: 2.5rem;
`;

const BackButton = styled.button`
  font-size: 0.875rem;
  color: #5ce9ff;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-bottom: 1.5rem;
  transition: color 0.2s;
  
  &:hover {
    color: #00f7d2;
  }
`;

const StoreTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: 600;
  margin: 0;
  color: white;
`;

const TraceabilityCard = styled.div`
  margin-top: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.75rem;
  padding: 1rem;
`;

const TraceabilityTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: white;
`;

const Timeline = styled.ul`
  position: relative;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  padding-left: 1.5rem;
  margin: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const TimelineItem = styled.li`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: -1.625rem;
    top: 0.25rem;
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 50%;
    background: #5ce9ff;
  }
`;

const TimelineLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  margin-bottom: 0.25rem;
`;

const TimelineMeta = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
  margin-bottom: 0.25rem;
`;

const TimelineLink = styled.a`
  font-size: 0.75rem;
  color: #5ce9ff;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const FiltersSection = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem 2.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterSelect = styled.select`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #5ce9ff;
  }
  
  option {
    background: #0B1122;
    color: white;
  }
`;

const FilterCheckbox = styled.label`
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  
  input[type="checkbox"] {
    cursor: pointer;
  }
`;

const SellerActions = styled.div`
  display: flex;
  gap: 1rem;
  padding: 0 2.5rem 1.5rem 2.5rem;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 40px;
  gap: 10px;

  button {
    background: rgba(255, 255, 255, 0.05);
    color: #e5e7eb;
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 8px 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: 0.25s ease;
    min-width: 40px;

    &:hover:not(:disabled) {
      border-color: #00f7d2;
      color: #00f7d2;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  span {
    color: #9ca3af;
    padding: 0 4px;
  }

  .active {
    border-color: #00f7d2;
    color: #00f7d2;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  padding: 0 2.5rem 3rem 2.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled.div`
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  height: 100%;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
  }
`;

const GroupCard = styled.div`
  border-radius: 0.5rem;
  background: ${props => props.$isGroup
    ? 'linear-gradient(90deg, rgba(139,92,246,0.18), rgba(139,92,246,0.05))'
    : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.$isGroup ? 'rgba(139,92,246,0.6)' : 'rgba(255, 255, 255, 0.1)'};
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
  display: flex;
  flex-direction: column;
  height: 100%;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
    border-color: ${props => props.$isGroup ? '#8b5cf6' : '#00f7d2'};
    background: ${props => props.$isGroup ? 'rgba(139,92,246,0.12)' : 'rgba(255, 255, 255, 0.08)'};
  }
`;

const ProductImage = styled.div`
  height: 200px;
  width: 100%;
  overflow: hidden;
  position: relative;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .placeholder-icon {
    width: 48px;
    height: 48px;
    opacity: 0.5;
  }
`;

const ProductContent = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-between;
`;

const ProductName = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: white;
  font-family: sans-serif;
`;

const ProductPrice = styled.span`
  font-size: 0.875rem;
  color: white;
  font-weight: 400;
  font-family: sans-serif;
  margin-bottom: 0.75rem;
`;

const ProductFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: auto;
`;

const ViewButton = styled.button`
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  border-radius: 0.25rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
  font-family: sans-serif;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const AuctionsSection = styled.div`
  padding: 0 2.5rem 4rem 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: white;
`;

const AuctionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AuctionCard = styled.div`
  padding: 1.5rem;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const AuctionTitle = styled.h4`
  font-weight: 500;
  margin: 0 0 0.5rem 0;
  color: white;
`;

const AuctionMeta = styled.p`
  font-size: 0.875rem;
  color: #9ca3af;
  margin: 0 0 1rem 0;
`;

const AuctionButton = styled.button`
  margin-top: 1rem;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  border-radius: 0.5rem;
  background: linear-gradient(to right, #00f7d2, #5ce9ff);
  color: black;
  border: none;
  cursor: pointer;
  font-weight: 500;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const slideIn = keyframes`
  from {
    transform: translateX(400px);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(400px);
  }
`;

const SidebarOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 1000;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const SidebarContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 420px;
  height: 100%;
  background: #0B1122;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  z-index: 1001;
  overflow-y: auto;
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(420px)'};
  transition: transform 0.3s ease;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const SidebarTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: white;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Label = styled.label`
  font-size: 0.75rem;
  color: #9ca3af;
`;

const Input = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem;
  border-radius: 0.25rem;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:focus {
    outline: none;
    border-color: #5ce9ff;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem;
  border-radius: 0.25rem;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  resize: vertical;
  min-height: 80px;
  
  &:focus {
    outline: none;
    border-color: #5ce9ff;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const SelectStyled = styled.select`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem;
  border-radius: 0.25rem;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:focus {
    outline: none;
    border-color: #5ce9ff;
  }
  
  option {
    background: #0B1122;
    color: white;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileUploadButton = styled.button`
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.25rem;
  color: white;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const ImageGrid = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
`;

const ImagePreview = styled.img`
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 0.25rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 2rem;
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background: linear-gradient(to right, #00f7d2, #5ce9ff);
  border: none;
  color: black;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const SelectWrapper = styled.div`
  .react-select-container {
    .react-select__control {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 0.25rem;
      min-height: 42px;
      
      &:hover {
        border-color: rgba(255, 255, 255, 0.2);
      }
    }
    
    .react-select__control--is-focused {
      border-color: #5ce9ff;
      box-shadow: 0 0 0 1px #5ce9ff;
    }
    
    .react-select__single-value,
    .react-select__placeholder {
      color: white;
      font-size: 0.875rem;
    }
    
    .react-select__input-container {
      color: white;
    }
    
    .react-select__menu {
      background: #0B1122;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    .react-select__option {
      background: #0B1122;
      color: white;
      font-size: 0.875rem;
      
      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
      
      &--is-selected {
        background: #5ce9ff;
        color: black;
      }
    }
  }
`;

const VariantTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  
  th, td {
    padding: 0.5rem;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    color: white;
  }
  
  th {
    font-weight: 600;
    color: #9ca3af;
  }
  
  button {
    background: #ff3b6b;
    border: none;
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    cursor: pointer;
    font-size: 0.7rem;
    
    &:hover {
      opacity: 0.9;
    }
  }
`;

const AddButton = styled.button`
  padding: 0.5rem 1rem;
  background: linear-gradient(to right, #00f7d2, #5ce9ff);
  border: none;
  color: black;
  border-radius: 0.25rem;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.875rem;
  transition: opacity 0.2s;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FeatureInputGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  
  input {
    flex: 1;
  }
  
  button {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(to right, #00f7d2, #5ce9ff);
    border: none;
    color: black;
    cursor: pointer;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    
    &:hover {
      opacity: 0.9;
    }
  }
`;

const VariantInputGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  
  input, select {
    flex: 1;
  }
`;

const StoreDetailView = () => {
  const history = useHistory();
  const { storeId } = useParams();
  const [storeData, setStoreData] = useState(null);
  console.log("🚀 ~ StoreDetailView ~ storeData:", storeData)
  const [products, setProducts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [lots, setLots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [productCount, setProductCount] = useState(0);
  const itemsPerPage = 50;
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterCertified, setFilterCertified] = useState(false);
  const [filterStock, setFilterStock] = useState(false);
  const [productSidebarOpen, setProductSidebarOpen] = useState(false);
  const [importSidebarOpen, setImportSidebarOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [auctionSidebarOpen, setAuctionSidebarOpen] = useState(false);
  const [showVariantFields, setShowVariantFields] = useState(false);
  const [categories, setCategories] = useState([]);
  const [allCategoriesData, setAllCategoriesData] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  const [isCreatingAuction, setIsCreatingAuction] = useState(false);

  // Product form state
  const [productTitle, setProductTitle] = useState('');
  const [productBrand, setProductBrand] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  console.log("🚀 ~ StoreDetailView ~ selectedSubcategory:", selectedSubcategory)
  const [selectedImages1, setSelectedImages1] = useState([]); // Array of File objects
  const [file2, setFile2] = useState([]); // Array of File objects for documents
  const [productCondition, setProductCondition] = useState('New');
  const [warrantyMonths, setWarrantyMonths] = useState("");
  const [vat, setVat] = useState("");
  const [deliveryCharges, setDeliveryCharges] = useState("");
  const [pickupAddress, setPickupAddress] = useState('');

  // Features
  const [fields, setFields] = useState([{ id: 1, value: "" }]);
  const [fieldsNew, setFieldsNew] = useState([]);

  // Product variants - dynamic fields from custom_detail
  const [variantFields, setVariantFields] = useState({});
  const [arrayOfArraysNew, setArrayOfArraysNew] = useState([]);

  // Get custom_detail from store category
  const customDetailFields = storeData?.category?.custom_detail || [];

  // Store type and agency
  const [selectedType, setSelectedType] = useState('seller');
  const [selectedAgencyPercentage, setSelectedAgencyPercentage] = useState(0);
  const [selectedWallet, setSelectedWallet] = useState(null);

  // Status flags
  const [preOrderStatus, setPreOrderStatus] = useState(false);
  const [offerStatus, setOfferStatus] = useState(false);

  // Auction form state
  const [auctionTitle, setAuctionTitle] = useState('');
  const [auctionBrand, setAuctionBrand] = useState('');
  const [auctionDescription, setAuctionDescription] = useState('');
  const [auctionStartTime, setAuctionStartTime] = useState('');
  const [auctionEndTime, setAuctionEndTime] = useState('');
  const [selectedAuctionCategory, setSelectedAuctionCategory] = useState(null);
  const [selectedAuctionSubcategory, setSelectedAuctionSubcategory] = useState(null);
  const [auctionProductCondition, setAuctionProductCondition] = useState('New');
  const [auctionPickupAddress, setAuctionPickupAddress] = useState('');
  const [auctionWarrantyMonths, setAuctionWarrantyMonths] = useState(0);
  const [auctionDeliveryCharges, setAuctionDeliveryCharges] = useState(0);
  const [auctionLogoFile, setAuctionLogoFile] = useState(null);
  const [auctionPdfFile, setAuctionPdfFile] = useState(null);
  const [auctionTiers, setAuctionTiers] = useState([]);
  const [auctionTierQuantity, setAuctionTierQuantity] = useState('');
  const [auctionTierPrice, setAuctionTierPrice] = useState('');
  const [auctionFeatures, setAuctionFeatures] = useState([{ id: 1, value: '' }]);

  const productImageInputRef = useRef(null);
  const productDocumentInputRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('user'));
  const userRole = user?.data?.user?.role || 'viewer';
  const userId = user?.data?.user?.user_id;

  // Check if user is the owner of the store
  const isStoreOwner = storeData && storeData.storeOwnerId === userId;

  useEffect(() => {
    setPage(1);
    fetchStoreDetails();
    fetchCategories();
  }, [storeId]);

  useEffect(() => {
    fetchProducts();
  }, [storeId, page]);

  const fetchStoreLots = useCallback(async () => {
    if (!storeId) return;
    try {
      const payload = { store_id: storeId };
      const response = await fetchMyStoreLot(payload);
      const raw = response?.data?.data || response?.data?.lots || [];
      const mapped = Array.isArray(raw) ? raw.map((lot) => {
        // Find the associated product to get its image, title and category
        const product = products.find((p) => p._id === lot.product_id);
        const productImage = product?.pictures?.[0] || product?.product_image || product?.image || product?.coverImage;
        const documentUrl = lot.documents?.[0] || null;

        // Derive a human readable category name from product category_detail if available
        let productCategoryLabel = null;
        if (product?.category_detail && Array.isArray(product.category_detail)) {
          const firstCategory = product.category_detail[0];
          if (firstCategory && Array.isArray(firstCategory.sub_categories) && firstCategory.sub_categories[0]) {
            productCategoryLabel = firstCategory.sub_categories[0].sub_category_name;
          }
        }
        if (!productCategoryLabel) {
          productCategoryLabel =
            product?.category_name?.[0]?.category_name ||
            product?.category_name ||
            null;
        }

        return {
          ...lot,
          isLot: true,
          _id: lot._id,
          title: product?.title || product?.name || `Lot #${lot.lot_number}`,
          pictures: productImage ? [productImage] : [],
          lot_number: lot.lot_number,
          product_id: lot.product_id,
          global_trade_item_number: lot.global_trade_item_number,
          compliance: lot.compliance || [],
          production: lot.production || [],
          documents: lot.documents || [],
          createdAt: lot.createdAt,
          updatedAt: lot.updatedAt,
          // Extra metadata used by PharmaLotView when opening from store
          productCategoryLabel,
        };
      }) : [];
      setLots(mapped);
    } catch (err) {
      console.error('Error fetching store lots:', err);
      setLots([]);
    }
  }, [storeId, products]);

  useEffect(() => {
    if (products.length > 0 || storeId) {
      fetchStoreLots();
    }
  }, [storeId, products, fetchStoreLots]);

  useEffect(() => {
    if (selectedCategory && allCategoriesData.length > 0) {
      const categoryData = allCategoriesData.find(cat => cat._id === selectedCategory.value);
      if (categoryData && categoryData.sub_categories && Array.isArray(categoryData.sub_categories)) {
        const subcatOptions = categoryData.sub_categories.map(sub => ({
          value: sub._id,
          label: sub.sub_category_name,
        }));
        setSubcategories(subcatOptions);
      } else {
        setSubcategories([]);
      }
    } else if (storeData) {
      if (storeData.category && storeData.category.sub_categories && Array.isArray(storeData.category.sub_categories)) {
        const subcatOptions = storeData.category.sub_categories.map(sub => ({
          value: sub._id,
          label: sub.sub_category_name,
        }));
        setSubcategories(subcatOptions);
      } else if (storeData.store_category_id && typeof storeData.store_category_id === 'object' && storeData.store_category_id.sub_categories && Array.isArray(storeData.store_category_id.sub_categories)) {
        const subcatOptions = storeData.store_category_id.sub_categories.map(sub => ({
          value: sub._id,
          label: sub.sub_category_name,
        }));
        setSubcategories(subcatOptions);
      } else if (storeData.categories && storeData.categories[0]?.sub_categories) {
        const subcatOptions = storeData.categories[0].sub_categories.map(sub => ({
          value: sub._id,
          label: sub.sub_category_name,
        }));
        setSubcategories(subcatOptions);
      } else {
        setSubcategories([]);
      }
    } else {
      setSubcategories([]);
    }
    setSelectedSubcategory(null);
  }, [selectedCategory, allCategoriesData, storeData]);

  const fetchCategories = async () => {
    try {
      const response = await getCategory();
      if (response.data?.category) {
        setAllCategoriesData(response.data.category);
        const options = response.data.category.map(cat => ({
          value: cat._id,
          label: cat.category_name,
        }));
        setCategories(options);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchStoreDetails = async () => {
    if (!storeId) return;

    try {
      setIsLoading(true);
      const response = await getStoreInfoById({ store_id: storeId });

      if (response.data?.result?.[0]) {
        setStoreData(response.data.result[0]);
      } else {
        // Try getMyStore if getStoreInfoById doesn't work
        const myStoreResponse = await getMyStore();
        if (myStoreResponse.data?.result) {
          const store = myStoreResponse.data.result.find(s => s._id === storeId);
          if (store) {
            setStoreData(store);
          } else {
            toast.error('Store not found');
            history.push('/storedetail');
          }
        } else {
          toast.error('Store not found');
          history.push('/storedetail');
        }
      }
    } catch (error) {
      console.error('Error fetching store details:', error);
      toast.error('Failed to load store details');
      history.push('/storedetail');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProducts = async () => {
    if (!storeId) return;

    try {
      const payload = {
        limit: itemsPerPage,
        pageNumber: page,
        store_id: storeId,
      };
      const response = await getMyProduct(payload);
      if (response.data?.products) {
        setProducts(response.data.products);
      }
      const count = response.data?.count ?? response.data?.products?.length ?? 0;
      setProductCount(count);

      // Store array = groups (group purchases). Products stay as products (no isGroup).
      if (response.data?.store && Array.isArray(response.data.store)) {
        const storeGroups = response.data.store.map((storeGroup) => {
          const firstProductId = storeGroup.product_details?.[0]?.product_id;
          const product = response.data.products?.find((p) => p._id === firstProductId);
          const pictures = product?.pictures || [];

          return {
            ...storeGroup,
            isGroup: true, // only group records get this; products never do
            _id: storeGroup._id,
            title: storeGroup.group_name || 'Group Purchase',
            pictures,
            start_time: storeGroup.start_time,
            end_time: storeGroup.end_time,
            description: storeGroup.group_name || 'Group purchase consortium',
            product_details: storeGroup.product_details || [],
            memberCount: storeGroup.invited_users?.length ?? storeGroup.invited_user?.length ?? storeGroup.user?.length ?? 0,
          };
        });
        setGroups(storeGroups);
      } else {
        setGroups([]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleProductImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validFiles = [];
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 5MB limit`);
        return;
      }

      if (!['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
        toast.error(`${file.name} is not a valid image type`);
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      setSelectedImages1([...selectedImages1, ...validFiles]);
    }
    e.target.value = '';
  };

  const handleProductDocumentUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validFiles = [];
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 10MB limit`);
        return;
      }

      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (!['pdf', 'doc', 'docx'].includes(fileExtension)) {
        toast.error(`${file.name} is not a valid document type`);
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      setFile2([...file2, ...validFiles]);
    }
    e.target.value = '';
  };

  const handleRemoveImage = (index) => {
    const updated = selectedImages1.filter((_, i) => i !== index);
    setSelectedImages1(updated);
  };

  const handleRemoveDocument = (index) => {
    const updated = file2.filter((_, i) => i !== index);
    setFile2(updated);
  };

  const handleChangeFeature = (id, value) => {
    const updatedFields = fields.map((field) => {
      if (field.id === id) {
        return { ...field, value };
      }
      return field;
    });
    setFields(updatedFields);

    // Update fieldsNew array
    const featureValues = updatedFields.map((field) => field.value).filter(v => v.trim() !== '');
    setFieldsNew(featureValues);
  };

  const handleAddFeature = () => {
    const newId = fields.length > 0 ? Math.max(...fields.map(f => f.id)) + 1 : 1;
    setFields([...fields, { id: newId, value: "" }]);
  };

  const handleRemoveFeature = (id) => {
    const updatedFields = fields.filter((field) => field.id !== id);
    setFields(updatedFields);

    // Update fieldsNew array
    const featureValues = updatedFields.map((field) => field.value).filter(v => v.trim() !== '');
    setFieldsNew(featureValues);
  };

  // Initialize variant fields when custom_detail changes
  useEffect(() => {
    if (customDetailFields.length > 0) {
      const initialFields = {};
      customDetailFields.forEach((field) => {
        const fieldKey = Object.keys(field)[0];
        initialFields[fieldKey] = '';
      });
      setVariantFields(initialFields);
    }
  }, [customDetailFields.length]);

  const handleVariantFieldChange = (fieldKey, value) => {
    setVariantFields(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const handleAddVariant = () => {
    // Validate all fields are filled
    const emptyFields = customDetailFields.filter(field => {
      const fieldKey = Object.keys(field)[0];
      return !variantFields[fieldKey] || variantFields[fieldKey].toString().trim() === '';
    });

    if (emptyFields.length > 0) {
      toast.error('Please fill all variant fields');
      return;
    }

    // Build variant object from custom_detail fields
    const variant = {
      discount_percentage: 0,
      sold_quantity: 0,
    };

    // Add all custom_detail fields to variant
    customDetailFields.forEach((field) => {
      const fieldKey = Object.keys(field)[0];
      const value = variantFields[fieldKey];
      // Try to parse as number if it's a valid number string, otherwise keep as string
      const numValue = Number(value);
      if (!isNaN(value) && value !== '' && !isNaN(numValue)) {
        variant[fieldKey] = value.includes('.') ? parseFloat(value) : parseInt(value, 10);
      } else {
        variant[fieldKey] = value;
      }
    });

    setArrayOfArraysNew([...arrayOfArraysNew, variant]);

    // Reset fields
    const resetFields = {};
    customDetailFields.forEach((field) => {
      const fieldKey = Object.keys(field)[0];
      resetFields[fieldKey] = '';
    });
    setVariantFields(resetFields);
  };

  const handleRemoveVariant = (index) => {
    const updated = arrayOfArraysNew.filter((_, i) => i !== index);
    setArrayOfArraysNew(updated);
  };

  const handleCreateProduct = async () => {
    // Validation
    if (!productTitle) {
      toast.error('Product title is required');
      return;
    }
    if (!productBrand) {
      toast.error('Brand is required');
      return;
    }
    if (!productDescription) {
      toast.error('Description is required');
      return;
    }
    if (!selectedSubcategory) {
      toast.error('Subcategory is required');
      return;
    }
    if (!pickupAddress) {
      toast.error('Pickup address is required');
      return;
    }
    if (selectedImages1.length === 0) {
      toast.error('At least one product image is required');
      return;
    }
    if (fieldsNew.length === 0) {
      toast.error('At least one feature is required');
      return;
    }
    if (arrayOfArraysNew.length === 0) {
      toast.error('At least one product variant (color, price, quantity) is required');
      return;
    }

    try {
      setIsCreatingProduct(true);

      const formData = new FormData();

      // Add images
      for (const file of selectedImages1) {
        formData.append('File', file);
      }

      // Add documents
      for (const file1 of file2) {
        formData.append('File2', file1);
      }

      // Get category_id from store data
      const categoryId = storeData.store_category_id?._id || storeData.categories?.[0]?._id || '';

      // Add form fields matching old flow
      formData.append('category_id', storeData?.category?._id);
      formData.append('approver_wallet_Address', storeData.approver_wallet_address || '');
      formData.append('pickupAddress', pickupAddress);
      formData.append('warrantyInMonths', parseInt(warrantyMonths, 10));
      formData.append('vat', vat || 0);
      formData.append('title', productTitle);
      formData.append('brand', productBrand);
      formData.append('allowedCountries', JSON.stringify(storeData.delivery_available_countries || []));
      formData.append('deliveryCharges', parseInt(deliveryCharges, 10));
      formData.append('isDeliveryServiceActive', storeData.online_delivery_available ? 'true' : 'false');
      formData.append('chainId', storeData.chainId || '');
      formData.append('symbol', storeData.symbol || 'USDT');
      formData.append('store_id', storeId);
      formData.append('seller_wallet', selectedType === 'seller' ? user?.data?.user?.address : (selectedWallet?.address || user?.data?.user?.address));
      formData.append('agency_percentage', selectedAgencyPercentage || 0);
      formData.append('description', productDescription);
      formData.append('product_sizes_quantity', JSON.stringify(arrayOfArraysNew));
      formData.append('features', JSON.stringify(fieldsNew));
      formData.append('store_category_id', categoryId);
      formData.append('subcategory_id', selectedSubcategory.value);
      formData.append('product_condition', productCondition);

      // Conditional custom_offer_status
      if (!preOrderStatus) {
        formData.append('custom_offer_status', offerStatus);
      }
      formData.append('pre_order_status', preOrderStatus);

      const response = await createProduct(formData);

      if (response.data) {
        toast.success('Product created successfully!', {
          position: toast.POSITION.TOP_RIGHT,
        });
        setProductSidebarOpen(false);
        resetProductForm();
        setPage(1);
        // Refresh products
        fetchProducts();
      } else {
        toast.error(response.status || 'Failed to create product', {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch (error) {
      console.error('Create Product Error: ', error.response);
      const errorMessages = error.response?.data?.Message || 'Failed to create product';
      toast.error(errorMessages, {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setIsCreatingProduct(false);
    }
  };

  const resetProductForm = () => {
    // Clean up object URLs before resetting
    selectedImages1.forEach(file => {
      if (file instanceof File) {
        try {
          const url = URL.createObjectURL(file);
          URL.revokeObjectURL(url);
        } catch (error) {
          console.error('Error revoking object URL:', error);
        }
      }
    });

    setProductTitle('');
    setProductBrand('');
    setProductDescription('');
    setSelectedSubcategory(null);
    setSelectedImages1([]);
    setFile2([]);
    setProductCondition('New');
    setWarrantyMonths(0);
    setDeliveryCharges(0);
    setPickupAddress('');
    setFields([{ id: 1, value: "" }]);
    setFieldsNew([]);
    setVariantFields({});
    setArrayOfArraysNew([]);
    setShowVariantFields(false);
    setSelectedType('seller');
    setSelectedAgencyPercentage(0);
    setSelectedWallet(null);
    setPreOrderStatus(false);
    setOfferStatus(false);
    if (productImageInputRef.current) productImageInputRef.current.value = '';
    if (productDocumentInputRef.current) productDocumentInputRef.current.value = '';
  };

  // Auction handlers
  const handleCloseAuctionSidebar = () => {
    setAuctionSidebarOpen(false);
    resetAuctionForm();
  };

  const resetAuctionForm = () => {
    setAuctionTitle('');
    setAuctionBrand('');
    setAuctionDescription('');
    setAuctionStartTime('');
    setAuctionEndTime('');
    setSelectedAuctionCategory(null);
    setSelectedAuctionSubcategory(null);
    setAuctionProductCondition('New');
    setAuctionPickupAddress('');
    setAuctionWarrantyMonths(0);
    setAuctionDeliveryCharges(0);
    setAuctionLogoFile(null);
    setAuctionPdfFile(null);
    setAuctionTiers([]);
    setAuctionTierQuantity('');
    setAuctionTierPrice('');
    setAuctionFeatures([{ id: 1, value: '' }]);
  };

  const handleAddAuctionTier = () => {
    if (!auctionTierQuantity || !auctionTierPrice) {
      toast.error('Please fill both quantity and price');
      return;
    }
    setAuctionTiers([...auctionTiers, { minQty: parseInt(auctionTierQuantity, 10), price: parseFloat(auctionTierPrice) }]);
    setAuctionTierQuantity('');
    setAuctionTierPrice('');
  };

  const handleRemoveAuctionTier = (index) => {
    setAuctionTiers(auctionTiers.filter((_, i) => i !== index));
  };

  const handleAddAuctionFeature = () => {
    const newId = auctionFeatures.length > 0 ? Math.max(...auctionFeatures.map(f => f.id)) + 1 : 1;
    setAuctionFeatures([...auctionFeatures, { id: newId, value: '' }]);
  };

  const handleRemoveAuctionFeature = (id) => {
    setAuctionFeatures(auctionFeatures.filter(f => f.id !== id));
  };

  const handleChangeAuctionFeature = (id, value) => {
    setAuctionFeatures(auctionFeatures.map(f => f.id === id ? { ...f, value } : f));
  };

  const handleCreateAuctionSubmit = async () => {
    // Validation
    if (!auctionTitle) {
      toast.error('Title is required');
      return;
    }
    if (!auctionBrand) {
      toast.error('Brand is required');
      return;
    }
    if (!auctionDescription) {
      toast.error('Description is required');
      return;
    }
    if (!auctionStartTime) {
      toast.error('Start time is required');
      return;
    }
    if (!auctionEndTime) {
      toast.error('End time is required');
      return;
    }
    if (!selectedAuctionCategory) {
      toast.error('Category is required');
      return;
    }
    if (!selectedAuctionSubcategory) {
      toast.error('Subcategory is required');
      return;
    }
    if (!auctionPickupAddress) {
      toast.error('Pickup address is required');
      return;
    }
    if (auctionTiers.length === 0) {
      toast.error('At least one tier is required');
      return;
    }
    if (!auctionLogoFile) {
      toast.error('Logo is required');
      return;
    }
    if (!auctionPdfFile) {
      toast.error('PDF document is required');
      return;
    }

    try {
      setIsCreatingAuction(true);

      // Create FormData
      const formData = new FormData();

      // Add files
      formData.append('File', auctionLogoFile);
      formData.append('File2', auctionPdfFile);

      // Get feature values
      const featureValues = auctionFeatures.map(f => f.value).filter(v => v.trim() !== '');

      // Build tier_details array
      const tierDetails = auctionTiers.map(tier => ({
        minQty: tier.minQty,
        price: tier.price,
      }));

      // Add all other fields
      const startDateISO = new Date(auctionStartTime).toISOString();
      console.log("🚀 ~ handleCreateAuctionSubmit ~ startDateISO:", typeof startDateISO)
      const endDateISO = new Date(auctionEndTime).toISOString();
      console.log("🚀 ~ handleCreateAuctionSubmit ~ endDateISO:", typeof endDateISO)
      formData.append('auction_start_time', new Date(startDateISO).toISOString());
      formData.append('auction_end_time', new Date(endDateISO).toISOString());
      formData.append('seller_wallet', user?.data?.user?.address || '');
      formData.append('tier_details', JSON.stringify(tierDetails));
      formData.append('subcategory_id', selectedAuctionSubcategory);
      formData.append('product_condition', auctionProductCondition);
      formData.append('category_id', selectedAuctionCategory);
      formData.append('pickupAddress', auctionPickupAddress);
      formData.append('warrantyInMonths', parseInt(auctionWarrantyMonths, 10));
      formData.append('title', auctionTitle);
      formData.append('brand', auctionBrand);
      formData.append('deliveryCharges', parseFloat(auctionDeliveryCharges));
      formData.append('description', auctionDescription);
      formData.append('features', JSON.stringify(featureValues));

      const response = await addAuction(formData);

      if (response.data?.success || response.status === 200) {
        toast.success('Auction created successfully!', {
          position: toast.POSITION.TOP_RIGHT,
        });
        handleCloseAuctionSidebar();
      } else {
        toast.error('Failed to create auction', {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch (error) {
      console.error('Error creating auction:', error);
      toast.error(error.response?.data?.Message || 'Failed to create auction', {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setIsCreatingAuction(false);
    }
  };

  // Cleanup effect for object URLs
  useEffect(() => {
    return () => {
      // Cleanup object URLs on unmount
      selectedImages1.forEach(file => {
        if (file instanceof File) {
          try {
            const url = URL.createObjectURL(file);
            URL.revokeObjectURL(url);
          } catch (error) {
            // Ignore errors during cleanup
          }
        }
      });
    };
  }, [selectedImages1]);

  // Build traceability timeline from documents
  const buildTraceabilityTimeline = () => {
    if (!storeData?.store_document) return [];

    const timeline = [];

    // Add document upload event if document exists
    if (storeData.store_document) {
      timeline.push({
        id: '1',
        type: 'upload',
        label: 'Store documents uploaded',
        date: new Date().toISOString(),
        actor: 'user',
        status: 'ok',
      });
    }

    return timeline;
  };

  const traceEvents = buildTraceabilityTimeline();

  // Filter products
  const filteredProducts = products.filter(product => {
    if (filterCategory !== 'all' && product.category !== filterCategory) return false;
    // Add more filters as needed
    return true;
  });

  // Combine products and groups for display
  const allItems = [...groups, ...filteredProducts];

  if (isLoading) {
    return (
      <LoadingOverlay>
        <Ring size={65} color="#5ce9ff" />
      </LoadingOverlay>
    );
  }

  if (!storeData) {
    return null;
  }

  return (
    <>
      <StoreDetailNewView
        storeData={storeData}
        products={products}
        groups={groups}
        lots={lots}
        isLoading={isLoading}
        onBack={() => history.push('/storedetail')}
        onAddProduct={() => setProductSidebarOpen(true)}
        onAddLot={() => history.push(`/storedetail/${storeId}/add-lot`)}
        onCreateGroup={() => history.push(`/storedetail/${storeId}/group-purchase`)}
        onImportProducts={() => {
          setImportFile(null);
          setImportSidebarOpen(true);
        }}
        onViewProductDetails={(item) => {
          if (item.isGroup) {
            history.push(`/groups/${item._id}`);
          } else if (item.isLot) {
            // Pass the mapped lot item via location state so PharmaLotView
            // can pre-populate product name and category even for non-owners.
            history.push(`/storedetail/${storeId}/lot/${item._id}`, {
              lotItem: item,
            });
          } else {
            history.push(`/productdetails?id=${item._id}`);
          }
        }}
        onCloneProduct={async (item) => {
          if (item?.isGroup) {
            toast.info('Cloning is not available for groups.');
            return;
          }
          const productId = item?._id;
          if (!productId) {
            toast.error('Product ID is missing.');
            return;
          }
          try {
            setIsLoading(true)
            await cloneProduct({ product_id: productId });
            toast.success('Product cloned successfully.');
            await fetchProducts();
            setIsLoading(false)
          } catch (err) {
            const msg = err?.response?.data?.Message ?? err?.response?.data?.message ?? err?.message ?? 'Failed to clone product';
            toast.error(msg);
            setIsLoading(false)
          }
        }}
        isStoreOwner={isStoreOwner}
      />
      <SidebarOverlay
        isOpen={productSidebarOpen}
        onClick={() => {
          setProductSidebarOpen(false);
          resetProductForm();
        }}
      />
      <SidebarContainer
        isOpen={productSidebarOpen}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <SidebarTitle style={{ margin: 0 }}>Add Product</SidebarTitle>
          <button
            onClick={() => {
              setProductSidebarOpen(false);
              resetProductForm();
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#9ca3af',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              e.target.style.color = '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.color = '#9ca3af';
            }}
          >
            ×
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <FormGroup>
            <Label>Product Title *</Label>
            <Input
              type="text"
              placeholder="Product title"
              value={productTitle}
              onChange={(e) => setProductTitle(e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>Brand *</Label>
            <Input
              type="text"
              placeholder="Brand name"
              value={productBrand}
              onChange={(e) => setProductBrand(e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>Description *</Label>
            <TextArea
              placeholder="Product description"
              value={productDescription}
              onChange={(e) => setProductDescription(e.target.value)}
              rows={3}
            />
          </FormGroup>

          <FormGroup>
            <Label>Subcategory *</Label>
            <SelectWrapper>
              <Select
                options={subcategories}
                value={selectedSubcategory}
                onChange={(option) => setSelectedSubcategory(option)}
                placeholder="Select subcategory"
                className="react-select-container"
                classNamePrefix="react-select"
                isClearable
              />
            </SelectWrapper>
          </FormGroup>

          <FormGroup>
            <Label>Delivery Charges *</Label>
            <Input
              type="number"
              placeholder="0"
              value={deliveryCharges}
              onChange={(e) => setDeliveryCharges(parseInt(e.target.value, 10) || 0)}

            />
          </FormGroup>

          <FormGroup>
            <Label>Pickup Address *</Label>
            <Input
              type="text"
              placeholder="Enter Pickup Address"
              value={pickupAddress}
              onChange={(e) => setPickupAddress(e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>Warranty In Months *</Label>
            <Input
              type="number"
              placeholder="0"
              value={warrantyMonths}
              onChange={(e) => setWarrantyMonths(parseInt(e.target.value, 10) || 0)}
            />
          </FormGroup>

          <FormGroup>
            <Label>VAT (%)</Label>
            <Input
              type="number"
              placeholder="Enter VAT percentage"
              value={vat ?? ""}
              onChange={(e) => setVat(e.target.value === "" ? "" : parseFloat(e.target.value))}
              min="0"
              max="100"
              step="0.01"
            />
            <span style={{ fontSize: "12px", color: "#9ca3af", marginTop: "4px", display: "block" }}>
              Enter the VAT rate as a percentage (e.g., 22 for 22%)
            </span>
          </FormGroup>

          {/* Product Images */}
          <FormGroup>
            <Label>Product Images *</Label>
            <FileUploadButton
              type="button"
              onClick={() => productImageInputRef.current?.click()}
            >
              Upload Images
            </FileUploadButton>
            <FileInput
              ref={productImageInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleProductImageUpload}
              multiple
            />
            <ImageGrid>
              {selectedImages1.map((file, i) => {
                let imageUrl = null;
                try {
                  if (file instanceof File) {
                    imageUrl = URL.createObjectURL(file);
                  } else if (typeof file === 'string') {
                    imageUrl = file;
                  }
                } catch (error) {
                  console.error('Error creating object URL:', error);
                }

                return imageUrl ? (
                  <div key={i} style={{ position: 'relative' }}>
                    <ImagePreview
                      src={imageUrl}
                      alt={`Product ${i + 1}`}
                      onError={(e) => {
                        console.error('Image load error:', e);
                        e.target.style.display = 'none';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (imageUrl && imageUrl.startsWith('blob:')) {
                          URL.revokeObjectURL(imageUrl);
                        }
                        handleRemoveImage(i);
                      }}
                      style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        background: '#ff3b6b',
                        border: 'none',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      ×
                    </button>
                  </div>
                ) : null;
              })}
            </ImageGrid>
          </FormGroup>

          {/* Product Documents */}
          <FormGroup>
            <Label>Product Document</Label>
            <FileUploadButton
              type="button"
              onClick={() => productDocumentInputRef.current?.click()}
            >
              Upload Document
            </FileUploadButton>
            <FileInput
              ref={productDocumentInputRef}
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleProductDocumentUpload}
              multiple
            />
            {file2.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                {file2.map((file, i) => (
                  <div key={i} style={{ fontSize: '0.75rem', color: '#5ce9ff', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {file.name}
                    <button
                      type="button"
                      onClick={() => handleRemoveDocument(i)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#ff3b6b',
                        cursor: 'pointer',
                        fontSize: '1rem',
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </FormGroup>

          {/* Features */}
          <FormGroup>
            <Label>Features *</Label>
            {fields.map((field) => (
              <FeatureInputGroup key={field.id}>
                <Input
                  type="text"
                  placeholder="Enter Features"
                  value={field.value}
                  onChange={(e) => handleChangeFeature(field.id, e.target.value)}
                />
                {fields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveFeature(field.id)}
                    style={{
                      background: '#ff3b6b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      cursor: 'pointer',
                      fontSize: '1.2rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    ×
                  </button>
                )}
                {field.id === fields[fields.length - 1].id && (
                  <button
                    type="button"
                    onClick={handleAddFeature}
                    style={{
                      background: 'linear-gradient(to right, #00f7d2, #5ce9ff)',
                      color: 'black',
                      border: 'none',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      cursor: 'pointer',
                      fontSize: '1.2rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                    }}
                  >
                    +
                  </button>
                )}
              </FeatureInputGroup>
            ))}
          </FormGroup>

          <FormGroup>
            <Label>Condition *</Label>
            <SelectStyled
              value={productCondition}
              onChange={(e) => setProductCondition(e.target.value)}
            >
              <option value="New">New</option>
              <option value="Used">Used</option>
              <option value="Refurbished">Refurbished</option>
            </SelectStyled>
          </FormGroup>

          <FormGroup>
            <Label>Custom Offer Status</Label>
            <SelectStyled
              value={offerStatus ? 'true' : 'false'}
              onChange={(e) => setOfferStatus(e.target.value === 'true')}
            >
              <option value="false">False</option>
              <option value="true">True</option>
            </SelectStyled>
          </FormGroup>

          <FormGroup>
            <Label>Pre Order Status</Label>
            <SelectStyled
              value={preOrderStatus ? 'true' : 'false'}
              onChange={(e) => setPreOrderStatus(e.target.value === 'true')}
            >
              <option value="false">False</option>
              <option value="true">True</option>
            </SelectStyled>
          </FormGroup>

          <FormGroup>
            <Label>Store Type</Label>
            <SelectStyled
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="seller">Seller</option>
              <option value="agency">Agency</option>
            </SelectStyled>
          </FormGroup>

          {/* Divider */}
          <div style={{
            margin: '24px 0',
            height: '1px',
            background: 'rgba(255, 255, 255, 0.1)',
            width: '100%'
          }} />

          {/* Product Variants Section */}
          <FormGroup>
            <Label>Product Variants</Label>
            <AddButton
              type="button"
              onClick={() => setShowVariantFields(!showVariantFields)}
              style={{ width: '100%', marginTop: '0.5rem' }}
            >
              <i className={`fa fa-${showVariantFields ? 'minus' : 'plus'}`} style={{ marginRight: '8px' }} />
              {showVariantFields ? 'Hide' : 'Add'} Product Variants
            </AddButton>
            {arrayOfArraysNew.length > 0 && (
              <div style={{ marginTop: '12px', fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }}>
                {arrayOfArraysNew.length} variant{arrayOfArraysNew.length !== 1 ? 's' : ''} added
              </div>
            )}
          </FormGroup>

          {/* Dynamic Variant Fields */}
          {showVariantFields && customDetailFields.length > 0 && (
            <>
              <div style={{
                margin: '20px 0',
                height: '1px',
                background: 'rgba(255, 255, 255, 0.1)',
                width: '100%'
              }} />
              <FormGroup>
                <Label style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '16px' }}>
                  Add Variant
                </Label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {customDetailFields.map((field, idx) => {
                    const fieldKey = Object.keys(field)[0];
                    const fieldLabel = field[fieldKey];
                    const isNumeric = ['price', 'quantity', 'year', 'tablets', 'vials', 'dosage'].includes(fieldKey.toLowerCase());

                    return (
                      <div key={idx}>
                        <Label style={{ marginBottom: '6px', display: 'block', textTransform: 'capitalize' }}>
                          {fieldLabel.replace(/_/g, ' ')}
                        </Label>
                        <Input
                          type={isNumeric ? 'number' : 'text'}
                          placeholder={`Enter ${fieldLabel.replace(/_/g, ' ')}`}
                          value={variantFields[fieldKey] || ''}
                          onChange={(e) => handleVariantFieldChange(fieldKey, e.target.value)}
                          min={isNumeric ? '0' : undefined}
                          step={fieldKey.toLowerCase().includes('price') ? '0.01' : undefined}
                        />
                      </div>
                    );
                  })}
                  <AddButton type="button" onClick={handleAddVariant} style={{ marginTop: '8px' }}>
                    Add Variant
                  </AddButton>
                </div>
              </FormGroup>

              {arrayOfArraysNew.length > 0 && (
                <>
                  <div style={{
                    margin: '20px 0',
                    height: '1px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    width: '100%'
                  }} />
                  <FormGroup>
                    <Label style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '12px' }}>
                      Added Variants
                    </Label>
                    <div style={{ background: '#0d1220', border: '1px solid #1f2937', borderRadius: '12px', overflow: 'hidden' }}>
                      <VariantTable style={{ marginTop: 0 }}>
                        <thead>
                          <tr>
                            {customDetailFields.map((field, idx) => {
                              const fieldKey = Object.keys(field)[0];
                              const fieldLabel = field[fieldKey];
                              return (
                                <th key={idx} style={{ textTransform: 'capitalize' }}>
                                  {fieldLabel.replace(/_/g, ' ')}
                                </th>
                              );
                            })}
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {arrayOfArraysNew.map((variant, index) => (
                            <tr key={index}>
                              {customDetailFields.map((field, idx) => {
                                const fieldKey = Object.keys(field)[0];
                                return (
                                  <td key={idx}>{variant[fieldKey] || '—'}</td>
                                );
                              })}
                              <td>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveVariant(index)}
                                  style={{
                                    padding: '6px 10px',
                                    background: 'rgba(239, 68, 68, 0.15)',
                                    border: '1px solid rgba(239, 68, 68, 0.25)',
                                    borderRadius: '6px',
                                    color: '#ef4444',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '32px',
                                    height: '32px',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.background = 'rgba(239, 68, 68, 0.25)';
                                    e.target.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.background = 'rgba(239, 68, 68, 0.15)';
                                    e.target.style.borderColor = 'rgba(239, 68, 68, 0.25)';
                                  }}
                                >
                                  <i className="fa fa-trash" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </VariantTable>
                    </div>
                  </FormGroup>
                </>
              )}
            </>
          )}
        </div>

        <ButtonGroup>
          <SaveButton onClick={handleCreateProduct} disabled={isCreatingProduct}>
            {isCreatingProduct ? 'Creating...' : 'Create Product'}
          </SaveButton>
          <CancelButton onClick={() => {
            setProductSidebarOpen(false);
            resetProductForm();
          }}>
            Cancel
          </CancelButton>
        </ButtonGroup>
      </SidebarContainer>

      {/* Import Products Sidebar */}
      <SidebarOverlay
        isOpen={importSidebarOpen}
        onClick={() => {
          if (!isImporting) {
            setImportSidebarOpen(false);
            setImportFile(null);
          }
        }}
      />
      <SidebarContainer
        isOpen={importSidebarOpen}
        onClick={(e) => e.stopPropagation()}
        style={{ paddingTop: '72px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <SidebarTitle style={{ margin: 0 }}>Import Products</SidebarTitle>
          <button
            type="button"
            onClick={() => {
              if (!isImporting) {
                setImportSidebarOpen(false);
                setImportFile(null);
              }
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#9ca3af',
              fontSize: '24px',
              cursor: isImporting ? 'not-allowed' : 'pointer',
              padding: 0,
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 6,
            }}
          >
            ×
          </button>
        </div>
        <FormGroup>
          <Label>File (JSON or CSV only, one file)</Label>
          <input
            type="file"
            accept=".json,.csv,application/json,text/csv,application/csv"
            style={{
              display: 'block',
              width: '100%',
              padding: '10px 12px',
              marginTop: 6,
              background: '#0d1220',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 8,
              color: '#e5e7eb',
              fontSize: 14,
            }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) {
                setImportFile(null);
                return;
              }
              const ext = file.name.split('.').pop()?.toLowerCase();
              const type = file.type?.toLowerCase() || '';
              const valid = ext === 'json' || ext === 'csv' || type.includes('json') || type.includes('csv');
              if (valid) {
                setImportFile(file);
              } else {
                toast.error('Only JSON or CSV files are allowed.');
                setImportFile(null);
                e.target.value = '';
              }
            }}
          />
          {importFile && (
            <p style={{ marginTop: 8, fontSize: 13, color: '#9ca3af' }}>
              Selected: {importFile.name}
            </p>
          )}
        </FormGroup>
        <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
          <button
            type="button"
            disabled={!importFile || isImporting}
            onClick={async () => {
              if (!importFile || !storeId || isImporting) return;
              const ext = importFile.name.split('.').pop()?.toLowerCase();
              if (ext !== 'json' && ext !== 'csv') {
                toast.error('Only JSON or CSV files are allowed.');
                return;
              }
              setIsImporting(true);
              try {
                const formData = new FormData();
                formData.append('File', importFile);
                formData.append('store_id', storeId);
                await importProduct(formData);
                toast.success('Products imported successfully.');
                setImportSidebarOpen(false);
                setImportFile(null);
                await fetchProducts();
              } catch (err) {
                const msg = err?.response?.data?.Message ?? err?.response?.data?.message ?? err?.message ?? 'Failed to import products';
                toast.error(msg);
              } finally {
                setIsImporting(false);
              }
            }}
            style={{
              flex: 1,
              padding: '12px 20px',
              borderRadius: 10,
              background: importFile && !isImporting ? 'linear-gradient(90deg, #00f7d2, #5ce9ff)' : 'rgba(255,255,255,0.1)',
              color: importFile && !isImporting ? '#000' : '#9ca3af',
              fontWeight: 700,
              border: 'none',
              cursor: importFile && !isImporting ? 'pointer' : 'not-allowed',
              fontSize: 14,
            }}
          >
            {isImporting ? 'Importing...' : 'Import'}
          </button>
          <button
            type="button"
            disabled={isImporting}
            onClick={() => {
              setImportSidebarOpen(false);
              setImportFile(null);
            }}
            style={{
              padding: '12px 20px',
              borderRadius: 10,
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#e5e7eb',
              fontWeight: 600,
              border: '1px solid rgba(255, 255, 255, 0.2)',
              cursor: isImporting ? 'not-allowed' : 'pointer',
              fontSize: 14,
            }}
          >
            Cancel
          </button>
        </div>
      </SidebarContainer>

      {/* Auction Creation Sidebar */}
      <SidebarOverlay
        isOpen={auctionSidebarOpen}
        onClick={handleCloseAuctionSidebar}
      />
      <SidebarContainer
        isOpen={auctionSidebarOpen}
        onClick={(e) => e.stopPropagation()}
      >
        <SidebarTitle>Create Auction</SidebarTitle>

        <FormGroup>
          <Label>Title *</Label>
          <Input
            type="text"
            value={auctionTitle}
            onChange={(e) => setAuctionTitle(e.target.value)}
            placeholder="Enter auction title"
          />
        </FormGroup>

        <FormGroup>
          <Label>Brand *</Label>
          <Input
            type="text"
            value={auctionBrand}
            onChange={(e) => setAuctionBrand(e.target.value)}
            placeholder="Enter brand name"
          />
        </FormGroup>

        <FormGroup>
          <Label>Description *</Label>
          <TextArea
            value={auctionDescription}
            onChange={(e) => setAuctionDescription(e.target.value)}
            placeholder="Enter auction description"
          />
        </FormGroup>

        <FormGroup>
          <Label>Start Time *</Label>
          <Input
            type="datetime-local"
            value={auctionStartTime}
            onChange={(e) => setAuctionStartTime(e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>End Time *</Label>
          <Input
            type="datetime-local"
            value={auctionEndTime}
            onChange={(e) => setAuctionEndTime(e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Category *</Label>
          <SelectStyled
            value={selectedAuctionCategory || ''}
            onChange={(e) => setSelectedAuctionCategory(e.target.value)}
          >
            <option value="">Select category</option>
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </SelectStyled>
        </FormGroup>

        <FormGroup>
          <Label>Subcategory *</Label>
          <SelectStyled
            value={selectedAuctionSubcategory || ''}
            onChange={(e) => setSelectedAuctionSubcategory(e.target.value)}
            disabled={!selectedAuctionCategory}
          >
            <option value="">Select subcategory</option>
            {subcategories.map(sub => (
              <option key={sub.value} value={sub.value}>{sub.label}</option>
            ))}
          </SelectStyled>
        </FormGroup>

        <FormGroup>
          <Label>Product Condition *</Label>
          <SelectStyled
            value={auctionProductCondition}
            onChange={(e) => setAuctionProductCondition(e.target.value)}
          >
            <option value="New">New</option>
            <option value="Used">Used</option>
            <option value="Refurbished">Refurbished</option>
          </SelectStyled>
        </FormGroup>

        <FormGroup>
          <Label>Pickup Address *</Label>
          <Input
            type="text"
            value={auctionPickupAddress}
            onChange={(e) => setAuctionPickupAddress(e.target.value)}
            placeholder="Enter pickup address"
          />
        </FormGroup>

        <FormGroup>
          <Label>Warranty (Months)</Label>
          <Input
            type="number"
            value={auctionWarrantyMonths}
            onChange={(e) => setAuctionWarrantyMonths(e.target.value)}
            min="0"
          />
        </FormGroup>

        <FormGroup>
          <Label>Delivery Charges</Label>
          <Input
            type="number"
            value={auctionDeliveryCharges}
            onChange={(e) => setAuctionDeliveryCharges(e.target.value)}
            min="0"
            step="0.01"
          />
        </FormGroup>

        <FormGroup>
          <Label>Logo *</Label>
          <FileUploadButton as="label">
            {auctionLogoFile ? auctionLogoFile.name : 'Choose Logo File'}
            <FileInput
              type="file"
              accept="image/*"
              onChange={(e) => setAuctionLogoFile(e.target.files[0])}
            />
          </FileUploadButton>
        </FormGroup>

        <FormGroup>
          <Label>PDF Document *</Label>
          <FileUploadButton as="label">
            {auctionPdfFile ? auctionPdfFile.name : 'Choose PDF File'}
            <FileInput
              type="file"
              accept="application/pdf"
              onChange={(e) => setAuctionPdfFile(e.target.files[0])}
            />
          </FileUploadButton>
        </FormGroup>

        <FormGroup>
          <Label>Pricing Tiers *</Label>
          <VariantInputGroup>
            <Input
              type="number"
              placeholder="Quantity"
              value={auctionTierQuantity}
              onChange={(e) => setAuctionTierQuantity(e.target.value)}
              min="1"
            />
            <Input
              type="number"
              placeholder="Price"
              value={auctionTierPrice}
              onChange={(e) => setAuctionTierPrice(e.target.value)}
              min="0"
              step="0.01"
            />
            <AddButton onClick={handleAddAuctionTier}>Add</AddButton>
          </VariantInputGroup>
          {auctionTiers.length > 0 && (
            <VariantTable>
              <thead>
                <tr>
                  <th>Quantity</th>
                  <th>Price</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {auctionTiers.map((tier, index) => (
                  <tr key={index}>
                    <td>{tier.minQty}+</td>
                    <td>€{tier.price}</td>
                    <td>
                      <button type="button" onClick={() => handleRemoveAuctionTier(index)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </VariantTable>
          )}
        </FormGroup>

        <FormGroup>
          <Label>Features</Label>
          {auctionFeatures.map(feature => (
            <FeatureInputGroup key={feature.id}>
              <Input
                type="text"
                value={feature.value}
                onChange={(e) => handleChangeAuctionFeature(feature.id, e.target.value)}
                placeholder="Enter feature"
              />
              <button type="button" onClick={() => handleRemoveAuctionFeature(feature.id)}>×</button>
            </FeatureInputGroup>
          ))}
          <AddButton onClick={handleAddAuctionFeature} style={{ marginTop: '0.5rem' }}>
            + Add Feature
          </AddButton>
        </FormGroup>

        <ButtonGroup>
          <CancelButton onClick={handleCloseAuctionSidebar}>Cancel</CancelButton>
          <SaveButton onClick={handleCreateAuctionSubmit} disabled={isCreatingAuction}>
            {isCreatingAuction ? 'Creating...' : 'Create Auction'}
          </SaveButton>
        </ButtonGroup>
      </SidebarContainer>
    </>
  );
};

export default StoreDetailView;
